import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import uuid4 from 'uuid/v4';
import classNames from 'classnames';
import throttle from 'lodash/throttle';
import Transition from 'react-transition-group/Transition';
import Vimeo from '@mediastorm/react-vimeo';
import PlayIcon from './PlayIcon';
import * as playerActions from '../actions/playerActions';


class Player extends Component {
  constructor (props) {
    super(props);
    this.state = {
      uid: null,
      dimensions: [16, 9],
    };

    this.handleLoaded = this.handleLoaded.bind(this);
    this.handlePlay = this.handlePlay.bind(this);
    this.handlePause = this.handlePause.bind(this);
    this.handleTimeUpdate = throttle(this.handleTimeUpdate.bind(this), 1000);
    this.handleSeek = this.handleSeek.bind(this);
    this.handleCuePoint = this.handleCuePoint.bind(this);
    this.handleEnd = this.handleEnd.bind(this);
    this.handlePosterClick = this.handlePosterClick.bind(this);
    this.handleGetDimensions = this.handleGetDimensions.bind(this);
    this.initPlayer = this.initPlayer.bind(this);
  }

  componentDidMount () {
    this.initPlayer();
  }

  UNSAFE_componentWillReceiveProps (nextProps) { // eslint-disable-line camelcase
    const { actions } = this.props;
    const { uid } = this.state;

    let currentPercent;
    let nextPercent;
    try {
      currentPercent = this.props.players[uid].playProgress.percent * 100;
      nextPercent = nextProps.players[uid].playProgress.percent * 100;
    } catch (e) {
      return null;
    }

    // Fire at every 10% mark
    if (!isNaN(currentPercent) && !isNaN(nextPercent) && nextPercent !== 0 && nextPercent % 10 < currentPercent % 10) {
      const progressPercent = Math.floor(nextPercent / 10) * 10;
      actions.playerLogProgress(uid, progressPercent);
    }
  }

  componentDidUpdate (prevProps, prevState) {
    if (this.props.video !== prevProps.video) {
      this.initPlayer();
    }
  }

  componentWillUnmount () {
    const { actions } = this.props;
    actions.playerRemove(this.state.uid);
  }

  handleLoaded () {
    const iframe = this.containerRef.querySelector('iframe');
    iframe.setAttribute('allow', 'autoplay');
  }

  handlePlay () {
    const { actions } = this.props;
    const { uid } = this.state;
    const instance = this.props.players[uid];
    if (Object.keys(instance.playProgress).length !== 0) {
      actions.playerToggle(uid, true);
    } else {
      actions.playerStart(uid);
    }
  }

  handlePause () {
    const { actions } = this.props;
    actions.playerToggle(this.state.uid, false);
  }

  handleTimeUpdate (progress) {
    const { actions } = this.props;
    actions.playerUpdateProgress(this.state.uid, progress);
  }

  handleSeek () {
    const { actions } = this.props;
    actions.playerSeekReset(this.state.uid);
  }

  handleCuePoint (data) {
    const { actions, onCuePoint } = this.props;
    actions.playerSetActiveCuePoint(this.state.uid, data);
    onCuePoint && onCuePoint(this.state.uid);
  }

  handleEnd () {
    const { actions, onEnd } = this.props;
    const { uid } = this.state;
    actions.playerLogProgress(uid, 100);
    // actions.playerUnload(uid);
    onEnd && onEnd(uid);
  }

  handlePosterClick () {
    const { actions } = this.props;
    actions.playerStart(this.state.uid);
  }

  handleGetDimensions (w, h) {
    this.setState({ dimensions: [w, h] });
  }

  initPlayer () {
    const { actions, onInit, video, slug } = this.props;
    const uid = uuid4();
    this.setState({ uid });
    actions.playerInit(uid, { videoId: video, videoSlug: slug });
    onInit && onInit(uid);
  }

  renderPosterOverlay (instance) {
    const { posterUrl, title, description } = this.props;
    if (!posterUrl) {
      return null;
    };

    let posterStyles = posterUrl ? { backgroundImage: `url('${posterUrl}')` } : null;

    let descriptionOverlay = null;
    if (title || description) {
      descriptionOverlay = (
        <div className="description-overlay" onClick={e => e.stopPropagation()}>
          {title ? <h5>{title}</h5> : ''}
          {description ? <p>{description}</p> : ''}
        </div>
      );
    }

    // Animation
    const duration = 500;
    posterStyles.transition = `opacity ${duration}ms ease-in-out`;
    posterStyles.opacity = 1;

    const transitionStyles = {
      entering: { opacity: 1 },
      exiting: { opacity: 0 },
      exited: { opacity: 0, visibility: 'hidden' },
    };

    return (
      <Transition in={!instance.posterHidden} timeout={duration}>
        {state => (
          <div className="poster-overlay"
            style={{ ...posterStyles, ...transitionStyles[state] }}
            onClick={this.handlePosterClick}>

            <PlayIcon />
            {descriptionOverlay}
          </div>
        )}
      </Transition>
    );
  }

  render () {
    const { uid, dimensions } = this.state;
    const instance = this.props.players[uid];
    if (!instance) {
      return null;
    }

    const {
      video,
      fullSize,
      showByline,
      color,
      showPortrait,
      showTitle,
      cuePoints,
      aspectRatio,
      autoplay,
    } = this.props;

    // If aspectRatio is not set explicitly, we fetch dimensions
    // from the Vimeo player API
    const [ w, h ] = aspectRatio ? aspectRatio.split(/[:/x]/) : dimensions;
    const wrapperStyles = !fullSize ? { paddingBottom: `${h / w * 100}%` } : null;

    const classes = classNames({
      'player-wrap': true,
      'full-size': fullSize,
    });

    return (
      <div className={classes} style={wrapperStyles} ref={node => this.containerRef = node}>
        <Vimeo
          className="vimeo-player"
          key={video} // Force rerender when the source video changes
          video={video}
          showByline={showByline}
          color={color}
          showPortrait={showPortrait}
          showTitle={showTitle}
          cuePoints={cuePoints}
          seekToTime={instance.seekTo}
          paused={!instance.isPlaying}
          onLoaded={this.handleLoaded}
          onPlay={this.handlePlay}
          onPause={this.handlePause}
          onSeeked={this.handleSeek}
          onCuePoint={this.handleCuePoint}
          onTimeUpdate={this.handleTimeUpdate}
          onEnd={this.handleEnd}
          getDimensions={this.handleGetDimensions}
          autoplay={autoplay}

        />

        {this.renderPosterOverlay(instance)}
      </div>
    );
  }
}

Player.propTypes = {
  onInit: PropTypes.func,
  onCuePoint: PropTypes.func,
  onEnd: PropTypes.func,
  autoplay: PropTypes.bool,
  players: PropTypes.object,
  actions: PropTypes.object,
  video: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
  posterUrl: PropTypes.string,
  slug: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  fullSize: PropTypes.bool,
  showByline: PropTypes.bool,
  color: PropTypes.string,
  showPortrait: PropTypes.bool,
  showTitle: PropTypes.bool,
  aspectRatio: PropTypes.string,
  cuePoints: PropTypes.arrayOf(PropTypes.shape({
    time: PropTypes.number,
    data: PropTypes.object,
  })),
};

Player.defaultProps = {
  fullSize: false,
  color: '#ffffff',
  showByline: false,
  showPortrait: false,
  showTitle: false,
};

const mapStateToProps = ({ players }, ownProps) => ({
  players,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(playerActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(Player);



// WEBPACK FOOTER //
// ./src/components/Player.js